import { supabase } from '../lib/supabase';
import type { 
  InvitationToken, 
  CreateInvitationRequest, 
  CreateInvitationResponse,
  ValidateInvitationResponse 
} from '../types';

/**
 * 招待管理サービス
 * PostgreSQL Functionsを使用して管理者招待機能を提供
 */
export class InvitationService {
  /**
   * 管理者招待を作成
   */
  static async createInvitation(
    email: string,
    organizationId: string,
    inviterUserId: string
  ): Promise<CreateInvitationResponse> {
    const { data, error } = await supabase
      .rpc('create_admin_invitation', {
        org_id: organizationId,
        invitee_email: email,
        inviter_user_id: inviterUserId
      });

    if (error) {
      console.error('Failed to create invitation:', error);
      
      // エラーメッセージの解析
      if (error.message.includes('Only admins can invite')) {
        throw new Error('管理者権限が必要です');
      }
      if (error.message.includes('Active invitation already exists')) {
        throw new Error('このメールアドレス宛の有効な招待が既に存在します');
      }
      
      throw new Error('招待の作成に失敗しました');
    }

    // 招待URLを生成
    const invitationUrl = this.generateInviteUrl(data.token);

    return {
      ...data,
      invitationUrl
    };
  }

  /**
   * 組織の招待一覧を取得
   */
  static async getOrganizationInvitations(
    organizationId: string,
    requestingUserId: string
  ): Promise<InvitationToken[]> {
    const { data, error } = await supabase
      .rpc('get_organization_invitations', {
        org_id: organizationId,
        requesting_user_id: requestingUserId
      });

    if (error) {
      console.error('Failed to fetch invitations:', error);
      
      if (error.message.includes('Only admins can view')) {
        throw new Error('管理者権限が必要です');
      }
      
      throw new Error('招待一覧の取得に失敗しました');
    }

    return data?.invitations || [];
  }

  /**
   * 招待トークンを検証
   */
  static async validateInvitationToken(token: string): Promise<ValidateInvitationResponse> {
    const { data, error } = await supabase
      .rpc('validate_invitation_token', {
        token: token
      });

    if (error) {
      console.error('Failed to validate invitation:', error);
      throw new Error('招待トークンの検証に失敗しました');
    }

    if (!data.valid) {
      return {
        valid: false,
        error: data.error || '無効な招待トークンです'
      };
    }

    return {
      valid: true,
      invitation: data
    };
  }

  /**
   * 招待を受諾（トークンを使用）
   */
  static async acceptInvitation(
    token: string,
    acceptingUserId: string
  ): Promise<void> {
    const { data, error } = await supabase
      .rpc('use_invitation_token', {
        token: token,
        accepting_user_id: acceptingUserId
      });

    if (error) {
      console.error('Failed to accept invitation:', error);
      
      if (error.message.includes('Invalid, expired, or already used')) {
        throw new Error('無効、期限切れ、または使用済みの招待トークンです');
      }
      if (error.message.includes('already a member')) {
        throw new Error('既にこの組織のメンバーです');
      }
      
      throw new Error('招待の受諾に失敗しました');
    }

    return data;
  }

  /**
   * 招待をキャンセル
   */
  static async cancelInvitation(
    invitationId: string,
    requestingUserId: string
  ): Promise<void> {
    const { error } = await supabase
      .rpc('cancel_invitation', {
        invitation_id: invitationId,
        requesting_user_id: requestingUserId
      });

    if (error) {
      console.error('Failed to cancel invitation:', error);
      
      if (error.message.includes('Only admins can cancel')) {
        throw new Error('管理者権限が必要です');
      }
      if (error.message.includes('already used')) {
        throw new Error('使用済みの招待はキャンセルできません');
      }
      if (error.message.includes('already inactive')) {
        throw new Error('既に無効化されています');
      }
      
      throw new Error('招待のキャンセルに失敗しました');
    }
  }

  /**
   * 招待URLを生成
   */
  static generateInviteUrl(token: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/accept-invitation/${token}`;
  }

  /**
   * メール招待機能（将来実装用のプレースホルダー）
   */
  static async sendInvitationEmail(
    email: string,
    invitationUrl: string,
    organizationName: string
  ): Promise<void> {
    // 招待URLをクリップボードにコピー
    // ユーザーが手動で招待URLを共有する方式
    console.log('Invitation URL for', email, ':', invitationUrl);
    
    // クリップボードにコピー
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(invitationUrl);
        console.log('Invitation URL copied to clipboard');
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  }
}

/**
 * 公開アクセストークン管理サービス
 */
export class PublicAccessService {
  /**
   * 公開アクセストークンを生成
   */
  static async generatePublicToken(
    organizationId: string,
    creatorUserId: string
  ): Promise<{ tokenId: string; token: string; url: string }> {
    const { data, error } = await supabase
      .rpc('generate_public_access_token', {
        org_id: organizationId,
        creator_user_id: creatorUserId
      });

    if (error) {
      console.error('Failed to generate public token:', error);
      
      if (error.message.includes('Only admins can generate')) {
        throw new Error('管理者権限が必要です');
      }
      
      throw new Error('公開トークンの生成に失敗しました');
    }

    // 完全なURLを生成
    const publicUrl = this.generatePublicUrl(data.token);

    return {
      ...data,
      url: publicUrl
    };
  }

  /**
   * 現在の公開トークンを取得
   */
  static async getCurrentPublicToken(
    organizationId: string,
    requestingUserId: string
  ): Promise<{ exists: boolean; token?: string; url?: string; accessCount?: number }> {
    const { data, error } = await supabase
      .rpc('get_current_public_token', {
        org_id: organizationId,
        requesting_user_id: requestingUserId
      });

    if (error) {
      console.error('Failed to get current public token:', error);
      
      if (error.message.includes('Only admins can access')) {
        throw new Error('管理者権限が必要です');
      }
      
      throw new Error('公開トークンの取得に失敗しました');
    }

    if (!data.exists) {
      return { exists: false };
    }

    // 完全なURLを生成
    const publicUrl = this.generatePublicUrl(data.token);

    return {
      exists: true,
      token: data.token,
      url: publicUrl,
      accessCount: data.access_count
    };
  }

  /**
   * 公開URLを生成
   */
  static generatePublicUrl(token: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/public/${token}`;
  }

  /**
   * QRコードデータURLを生成（外部ライブラリ使用）
   */
  static async generateQRCode(url: string): Promise<string> {
    // TODO: qrcode.jsなどのライブラリを使用してQRコード生成
    // 現在はプレースホルダー
    return url;
  }
}